import { inject } from '@angular/core';
import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AUTH_CONFIG } from './auth.config';
import { AuthService } from './auth.service';
import { StorageService } from './storage.service';
import { TokenStore } from './token.store';

/**
 * Opt-out for calls that must see the raw HttpErrorResponse (status + body) and must NOT bounce
 * the app to login on 401. identity is keyed by mercury ids and 401s for anything else; a
 * checklist row or a form field should show that, not log the user out.
 *   this.http.get(url, { context: new HttpContext().set(RAW_HTTP_ERROR, true) })
 */
export const RAW_HTTP_ERROR = new HttpContextToken<boolean>(() => false);

/** what the caller receives on a 403 the server did not explain: a role gate, not a fault */
export const FORBIDDEN_MESSAGE = "You don't have access to this.";
/** what the caller receives when the session could not be refreshed; the login page says the same */
export const SESSION_ENDED_MESSAGE = 'Your session ended. Sign in again.';
/** `?reason=` on the login route after a forced logout, so the login page can say why */
export const LOGOUT_REASON_EXPIRED = 'expired';

/**
 * Last in the chain. Turns an HttpErrorResponse into a message string for the caller, and
 * reads 401 and 403 as the two different sentences they are (VT, 2026-09-04):
 *
 *  - 403: you are who you say; this route is not granted to your role. No refresh, no logout.
 *    The caller gets the server's reason, or FORBIDDEN_MESSAGE when the body is empty (the
 *    security filter's 403 has none), and shows it where the action was.
 *  - 401 with a session: the access token is stale, or the account was closed. One refresh,
 *    then the SAME request again, and the caller receives the retried answer as if nothing
 *    happened. Only a failed refresh, or a 401 on the retry, ends the session: logout to
 *    loginPath with `?reason=expired`, and the caller gets SESSION_ENDED_MESSAGE.
 *  - 401 without a session: a message, nothing else; the app's own guards send to login.
 *
 * The sign-in and refresh calls themselves are never retried here: a 401 on the refresh route
 * would otherwise refresh again, without end.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const config = inject(AUTH_CONFIG);
  const router = inject(Router);
  const storage = inject(StorageService);
  const auth = inject(AuthService);
  const tokens = inject(TokenStore);

  const isAuthCall = req.url.includes(config.signInPath) || req.url.includes(config.refreshPath);

  const endSession = (): Observable<never> => {
    auth.logOut();
    router.navigate([config.loginPath], { queryParams: { reason: LOGOUT_REASON_EXPIRED } }).then(() => {
      if (config.reloadOnLogout) window.location.reload();
    });
    return throwError(() => SESSION_ENDED_MESSAGE);
  };

  const withToken = () => {
    const accessToken = tokens.accessToken();
    return accessToken ? req.clone({ setHeaders: { access_token: accessToken.token } }) : req;
  };

  /** One refresh, then the same request with the new token, handed to the caller. A failed refresh ends the session. */
  const refreshAndRetry = () =>
    auth.refreshToken().pipe(
      switchMap(fresh => {
        if (!fresh) return endSession();
        return next(withToken()).pipe(
          catchError((retryErr: HttpErrorResponse) => {
            if (retryErr.status === HttpStatusCode.Unauthorized) return endSession();
            if (retryErr.status === HttpStatusCode.Forbidden) return throwError(() => serverReason(retryErr) ?? FORBIDDEN_MESSAGE);
            return throwError(() => messageOf(retryErr));
          }),
        );
      }),
    );

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      if (req.context.get(RAW_HTTP_ERROR)) return throwError(() => err);

      if (err.status === HttpStatusCode.Forbidden) {
        return throwError(() => serverReason(err) ?? FORBIDDEN_MESSAGE);
      }

      if (err.status === HttpStatusCode.Unauthorized && storage.getUser() != null && !isAuthCall) {
        return refreshAndRetry();
      }

      return throwError(() => messageOf(err));
    }),
  );
};

/** the server's own reason, from either odin envelope or a flat body; undefined when the body carries none */
function serverReason(err: HttpErrorResponse): string | undefined {
  const body = err.error;
  return body?.error?.message || body?.errors?.[0]?.reason || body?.message || undefined;
}

/**
 * odin ships two error envelopes; try both before falling back to statusText, otherwise a real
 * server reason degrades to "Unprocessable Entity".
 */
function messageOf(err: HttpErrorResponse): string {
  return serverReason(err) || err.statusText;
}
